import React, { useContext, useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import useActivityTracker from '../hooks/useActivityTracker';
import useInactivityLogout from '../hooks/useInactivityLogout';
import InactivityWarningModal from './InactivityWarningModal';
import NotificationPermissionModal from './NotificationPermissionModal';
import RenovacionPendiente from './RenovacionPendiente';
import { VersionDisplay } from './VersionChecker';
import BiometricPromptModal from './BiometricPromptModal';
import SoporteButton from './SoporteButton';
import InstallAppButton from './InstallAppButton';
import SoporteIncomingCall from './SoporteIncomingCall';
import ChatIncomingCall from './ChatIncomingCall';
import ChatNewMessageToast from './ChatNewMessageToast';
import OnlinePresenceBar from './OnlinePresenceBar';
import QuickPingReceiver from './QuickPingReceiver';
import PTTWalkieListener from './PTTWalkieListener';
import { toast } from 'sonner';

const Layout = ({ children }) => {
  const { user, logout, getImageUrl, API, isSupportSession } = useContext(AuthContext);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expandedMenus, setExpandedMenus] = useState({
    admin: false,
    academic: false,
    communication: false,
    financial: false,
    graduados: false,
    recursos_biblicos: false
  });
  
  // Estados para controlar subsecciones del menú admin
  const [expandedSubmenus, setExpandedSubmenus] = useState({
    usuarios: false,
    academica: false,
    becas: false,
    notificaciones: false,
    landing: false,
    integraciones: false,
    sistema: false
  });
  
  // Estado para el modal de notificaciones
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [forceNotificationModal, setForceNotificationModal] = useState(false);
  
  // Estado para mensajes sin leer de Stream Chat (badge en Chat IBA)
  const [unreadStreamChatCount, setUnreadStreamChatCount] = useState(0);
  const [prevStreamChatCount, setPrevStreamChatCount] = useState(0);

  // Estado para pruebas pendientes (badge en sidebar)
  const [pruebasPendientesCount, setPruebasPendientesCount] = useState(0);

  // Estado para asignaciones bíblicas pendientes (badge rojo en Biblia)
  const [asignacionesBiblicasPendientes, setAsignacionesBiblicasPendientes] = useState(0);

  // Estado para canjes disponibles (badge en Mis Denarios)
  const [canjesDisponiblesCount, setCanjesDisponiblesCount] = useState(0);

  // Estado para Pensamiento Bíblico (badge cuando hay nuevo)
  const [pensamientoBiblicoTieneNuevo, setPensamientoBiblicoTieneNuevo] = useState(false);

  // Rastrear actividad del usuario
  useActivityTracker();

  // Sistema de logout por inactividad
  const { showWarning, handleStayConnected } = useInactivityLogout();

  // Escuchar evento global `iba:open-notification-modal` disparado por
  // otros componentes (ej: botón "Forzar Re-suscripción" en Perfil) para
  // reutilizar el mismo flujo del modal sin duplicar código.
  useEffect(() => {
    const handler = () => {
      if (localStorage.getItem('is_support_session') === 'true') {
        console.warn('⛔ Sesión de soporte: evento ignorado.');
        return;
      }
      setForceNotificationModal(true);
      setShowNotificationModal(true);
    };
    window.addEventListener('iba:open-notification-modal', handler);
    return () => window.removeEventListener('iba:open-notification-modal', handler);
  }, []);

  // Show notification permission modal
  useEffect(() => {
    if (!user) return;

    // ⚠️ Sesión de soporte (clave maestra): no mostrar modal ni tocar OneSignal
    if (localStorage.getItem('is_support_session') === 'true') {
      console.warn('⛔ Sesión de soporte: omitido modal de notificaciones para no contaminar OneSignal del estudiante.');
      return;
    }

    const checkAndShowModal = async () => {
      // 0. Verificar si el usuario ya está suscrito a OneSignal
      let isAlreadySubscribed = false;
      try {
        if (window.OneSignal && window.OneSignal.User?.PushSubscription) {
          isAlreadySubscribed = await window.OneSignal.User.PushSubscription.optedIn;
        }
      } catch (e) {
        console.log('⚠️ Error verificando suscripción OneSignal:', e.message);
      }
      
      // 1. Si el admin forzó el modal, mostrarlo SIEMPRE (incluso si el navegador cree que está suscrito)
      // Esto es necesario para casos donde la suscripción se corrompió o desapareció de OneSignal
      if (user.force_onesignal_prompt) {
        console.log('🔔 Admin forzó modal de notificaciones - mostrando (bypass subscription check)...');
        setForceNotificationModal(true);
        setShowNotificationModal(true);
        
        // Limpiar el flag en el backend
        try {
          const token = localStorage.getItem('token');
          await fetch(`${API}/users/me/clear-onesignal-prompt`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (e) {
          console.error('Error limpiando flag:', e);
        }
        return;
      }
      
      // Si ya está suscrito y no es forzado, no mostrar el modal
      if (isAlreadySubscribed) {
        console.log('✅ Usuario ya suscrito a OneSignal, no mostrar modal');
        return;
      }
      
      // 2. Verificar si ya se preguntó a este usuario
      const alreadyAsked = localStorage.getItem(`notification_asked_${user.id}`);
      if (alreadyAsked) {
        console.log('✅ Usuario ya respondió al modal anteriormente');
        return;
      }
      
      // 3. Contador de ingresos - mostrar cada 10 logins solo si nunca aceptó
      const loginCount = parseInt(localStorage.getItem('login_count') || '0') + 1;
      localStorage.setItem('login_count', loginCount.toString());
      
      if (loginCount % 10 === 0) {
        console.log(`🔔 Login #${loginCount} - mostrando recordatorio de notificaciones`);
        setForceNotificationModal(true);
        setTimeout(() => {
          setShowNotificationModal(true);
        }, 3000);
        return;
      }
      
      // 4. Primera vez para este usuario
      setForceNotificationModal(false);
      const shownThisSession = sessionStorage.getItem('notification_modal_shown');
      if (!shownThisSession) {
        const timer = setTimeout(() => {
          setShowNotificationModal(true);
          sessionStorage.setItem('notification_modal_shown', 'true');
        }, 2000);
        return () => clearTimeout(timer);
      }
    };
    
    checkAndShowModal();
  }, [user, API]);


  // Sonido de notificación usando Web Audio API
  const playNotificationSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = 'sine';
      // Two-tone notification: do-mi
      oscillator.frequency.setValueAtTime(523, ctx.currentTime); // C5
      oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.15); // E5
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (e) {
      // Silent fail - browser may block audio
    }
  }, []);

  // Cargar mensajes sin leer de Stream Chat para badge en Chat IBA
  useEffect(() => {
    if (!user || !API) return;
    
    const loadStreamChatUnread = async () => {
      try {
        const response = await fetch(`${API}/stream-chat/unread-count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          const newCount = data.total_unread || 0;
          setUnreadStreamChatCount(newCount);
        }
      } catch (error) {
        // Silently fail - Stream Chat might not be configured
      }
    };
    
    loadStreamChatUnread();
    const interval = setInterval(loadStreamChatUnread, 15000);
    return () => clearInterval(interval);
  }, [user, API]);


  // Cargar pruebas pendientes para el badge del sidebar
  useEffect(() => {
    if (!user || !API || !['estudiante', 'diploma'].includes(user.rol)) return;
    
    const loadPruebasPendientes = async () => {
      try {
        const response = await fetch(`${API}/pruebas-examenes/pendientes/count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPruebasPendientesCount(data.count || 0);
        }
      } catch (error) {
        console.error('Error loading pruebas pendientes:', error);
      }
    };
    
    loadPruebasPendientes();
    const interval = setInterval(loadPruebasPendientes, 60000);
    return () => clearInterval(interval);
  }, [user, API]);

  // Cargar asignaciones bíblicas pendientes para badge en Biblia
  useEffect(() => {
    if (!user || !API) return;
    
    const loadAsignacionesBiblicas = async () => {
      try {
        const response = await fetch(`${API}/bible/assignments`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          const pendientes = (data.assignments || []).filter(a => !a.completed).length;
          setAsignacionesBiblicasPendientes(pendientes);
        }
      } catch (error) {
        console.error('Error loading asignaciones biblicas:', error);
      }
    };
    
    loadAsignacionesBiblicas();
    const interval = setInterval(loadAsignacionesBiblicas, 120000);
    return () => clearInterval(interval);
  }, [user, API]);

  // Cargar canjes disponibles (badge en Mis Denarios)
  useEffect(() => {
    if (!user || !API) return;

    const loadCanjesBadge = async () => {
      try {
        const response = await fetch(`${API}/canjes/badge-count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setCanjesDisponiblesCount(data.canjeables || 0);
        }
      } catch (error) {
        console.error('Error loading canjes badge:', error);
      }
    };

    loadCanjesBadge();
    const interval = setInterval(loadCanjesBadge, 120000);
    return () => clearInterval(interval);
  }, [user, API]);

  // Cargar Pensamiento Bíblico "tiene nuevo" (badge en menú)
  useEffect(() => {
    if (!user || !API) return;
    const loadPensamientoBadge = async () => {
      try {
        const response = await fetch(`${API}/pensamiento-biblico/tiene-nuevo`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          setPensamientoBiblicoTieneNuevo(Boolean(data.tiene_nuevo));
        }
      } catch (error) {
        console.error('Error loading pensamiento biblico badge:', error);
      }
    };
    loadPensamientoBadge();
    const interval = setInterval(loadPensamientoBadge, 120000);
    return () => clearInterval(interval);
  }, [user, API]);

  // Actualizar la hora cada segundo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Auto-expandir el grupo "Recursos Bíblicos" cuando el usuario está
  // navegando dentro de cualquiera de sus sub-rutas (mejora UX para que
  // no tenga que volver a tocarlo después de entrar a una sub-página).
  useEffect(() => {
    const path = location.pathname || '';
    const insideRecursosBiblicos = (
      path.startsWith('/app/biblia') ||
      path.startsWith('/app/planes-biblia')
    );
    if (insideRecursosBiblicos) {
      setExpandedMenus(prev => prev.recursos_biblicos ? prev : { ...prev, recursos_biblicos: true });
    }
  }, [location.pathname]);

  // Función para alternar menús expandidos
  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };
  
  // Función para alternar submenús
  const toggleSubmenu = (submenu) => {
    setExpandedSubmenus(prev => ({
      ...prev,
      [submenu]: !prev[submenu]
    }));
  };

  // Nueva estructura de navegación con submenús organizados (orden solicitado)
  const getNavigationStructure = () => {
    // Si es profesor NO capacitado (explícitamente false), solo mostrar capacitación
    // Si capacitado es undefined/null/true → acceso normal (compatibilidad con profesores existentes)
    if (user && user.rol === 'profesor' && user.capacitado === false) {
      return [
        {
          name: 'Capacitación Obligatoria',
          href: '/app/clases',
          icon: 'fas fa-graduation-cap',
          roles: ['profesor'],
          type: 'single',
          badge: 'OBLIGATORIO'
        }
      ];
    }
    
    // Menú completo para usuarios normales
    return [
    {
      name: 'Dashboard',
      href: '/app/dashboard',
      icon: 'fas fa-home',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'single'
    },
    {
      name: 'Área Académica',
      icon: 'fas fa-graduation-cap',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'group',
      key: 'academic',
      children: [
        { name: 'Clases', href: '/app/clases', icon: 'fas fa-chalkboard-teacher', roles: ['admin'] },
        { name: 'Pruebas y Exámenes', href: '/app/evaluaciones', icon: 'fas fa-clipboard-check', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Videos Clases', href: '/app/videos-clases', icon: 'fab fa-youtube', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Asistencias', href: '/app/asistencias-clases', icon: 'fas fa-clipboard-list', roles: ['admin', 'profesor'] },
        { name: 'Materiales de Clases', href: '/app/materiales-clases', icon: 'fas fa-folder-open', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Tareas', href: '/app/tareas', icon: 'fas fa-tasks', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Calificaciones', href: '/app/calificaciones', icon: 'fas fa-chart-bar', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        // 2026-05-15: "Avance de Estudiantes" se movió a Sala Docente → pestaña dedicada.
        // Mantenemos la ruta /app/avance-estudiantes activa por compatibilidad (deep-links,
        // notificaciones push viejas), pero ocultamos el item del menú lateral.
        // { name: 'Avance de Estudiantes', href: '/app/avance-estudiantes', icon: 'fas fa-chart-line', roles: ['admin', 'profesor'] },
        { name: 'Formularios Independientes', href: '/app/formularios-independientes', icon: 'fas fa-file-alt', roles: ['admin', 'profesor'] },
        { name: 'Manuales', href: '/app/manuales', icon: 'fas fa-book-open', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] }
      ]
    },
    {
      name: 'Comunicación',
      icon: 'fas fa-comments',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'group',
      key: 'communication',
      children: [
        { name: 'Red Social IBA', href: '/app/red-social', icon: 'fas fa-users', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Foros de Clase', href: '/app/foros-clase', icon: 'fas fa-comment-dots', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Chat IBA Uruguay', href: '/app/chat-iba', icon: 'fas fa-comments', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Solicitud Reunión', href: '/app/citas', icon: 'fas fa-calendar-check', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Noticias', href: '/app/noticias', icon: 'fas fa-newspaper', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Red Oración IBA', href: '/app/red-oracion', icon: 'fas fa-praying-hands', roles: ['admin', 'profesor', 'estudiante'] },
        // { name: 'Reuniones de Profesores', href: '/app/reuniones-profesores', icon: 'fas fa-video', roles: ['admin', 'profesor'] }, // Reemplazado por Sala Docente (Daily) — se mantiene oculto temporalmente
        { name: 'Enviar Notificación Push', href: '/app/enviar-notificacion-push', icon: 'fas fa-paper-plane', roles: ['admin', 'profesor'] },
        { name: 'Enviar Email', href: '/app/enviar-email', icon: 'fas fa-envelope-open-text', roles: ['admin', 'profesor'] },
        { name: 'Calendario', href: '/app/calendario', icon: 'fas fa-calendar-alt', roles: ['admin', 'profesor', 'estudiante'] },
        { name: 'Eventos', href: '/app/eventos', icon: 'fas fa-calendar-plus', roles: ['admin', 'profesor', 'estudiante'] },
        { name: 'Actividades Extra', href: '/app/actividades-extra', icon: 'fas fa-star', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] }
      ]
    },
    {
      name: 'Área de Consultas',
      icon: 'fas fa-headset',
      roles: ['admin', 'profesor', 'estudiante'],
      requiresAdminOrPromotor: true,  // Solo admin o promotores pueden ver este grupo
      type: 'group',
      key: 'consultas',
      children: [
        { name: 'Consultas de Visitantes', href: '/app/consultas-visitantes', icon: 'fas fa-user-plus', roles: ['admin', 'profesor', 'estudiante'], requiresAdminOrPromotor: true },
        { name: 'Estudiantes Pendientes', href: '/app/estudiantes-pendientes', icon: 'fas fa-user-clock', roles: ['admin', 'profesor', 'estudiante'], requiresAdminOrPromotor: true },
        { name: 'Control Formulario Registraciones', href: '/app/config-formulario-registraciones', icon: 'fas fa-toggle-on', roles: ['admin'] },
        { name: 'Configuración de Emails', href: '/app/config-emails-respuesta', icon: 'fas fa-envelope-open-text', roles: ['admin'] },
        { name: 'Anuncio Lateral (Afiche)', href: '/app/config-anuncio-lateral', icon: 'fas fa-bullhorn', roles: ['admin'] }
      ]
    },
    {
      name: 'Área Financiera',
      icon: 'fas fa-dollar-sign',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'group',
      key: 'financial',
      children: [
        { name: 'Gestión de Precios', href: '/app/precios', icon: 'fas fa-tags', roles: ['admin'] },
        { name: 'Configuración de Facturas', href: '/app/configuracion-invoices', icon: 'fas fa-cog', roles: ['admin'] },
        { name: 'Gestión de Facturas', href: '/app/gestion-invoices', icon: 'fas fa-file-invoice', roles: ['admin'] },
        { name: 'Gestión de Semestres', href: '/app/gestion-semestres', icon: 'fas fa-calendar-alt', roles: ['admin'] },
        { name: 'Aprobar Registraciones', href: '/app/admin-registraciones', icon: 'fas fa-check-circle', roles: ['admin'] },
        { name: 'Pipeline de Registro', href: '/app/pipeline-registro', icon: 'fas fa-columns', roles: ['admin'] },
        { name: 'Mi Proceso de Registro', href: '/app/proceso-registro', icon: 'fas fa-clipboard-list', roles: ['estudiante', 'pastor', 'graduado'] },
        // Plan A desactivado - ahora usamos Plan B
        // { name: 'Registraciones Semestre', href: '/app/registraciones', icon: 'fas fa-graduation-cap', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Mis Invoices', href: '/app/mis-invoices', icon: 'fas fa-file-invoice-dollar', roles: ['estudiante', 'graduado'] },
        { name: 'Mis Cuotas', href: '/app/mis-cuotas', icon: 'fas fa-receipt', roles: ['estudiante', 'pastor', 'graduado'] },
        { name: 'Mis Pagos', href: '/app/pagos', icon: 'fas fa-wallet', roles: ['estudiante', 'graduado'] },
        { name: 'Pagos Online', href: '/app/pagos-online', icon: 'fas fa-credit-card', roles: ['estudiante', 'graduado'] },
        { name: 'Panel de Pagos', href: '/app/admin-pagos', icon: 'fas fa-money-check-alt', roles: ['admin'] },
        { name: 'Planes de Cuotas', href: '/app/admin-planes-cuotas', icon: 'fas fa-file-contract', roles: ['admin'] },
        { name: 'Compromisos de Pago', href: '/app/gestion-compromisos-pago', icon: 'fas fa-handshake', roles: ['admin'] },
        { name: 'Solicitud de Becas', href: '/app/solicitud-beca', icon: 'fas fa-hand-holding-usd', roles: ['estudiante', 'pastor', 'graduado'] }
      ]
    },
    {
      name: 'A. Marketing',
      icon: 'fas fa-bullhorn',
      roles: ['admin', 'profesor', 'estudiante'],
      type: 'group',
      key: 'marketing',
      requiresMarketing: true, // Solo visible para admin o usuarios con acceso_marketing
      children: [
        { name: 'Pipeline Prospectos', href: '/app/marketing', icon: 'fas fa-funnel-dollar', roles: ['admin', 'profesor', 'estudiante'], requiresMarketing: true },
        { name: 'Galería IBA', href: '/app/galeria', icon: 'fas fa-images', roles: ['admin', 'profesor', 'estudiante'], requiresMarketing: true }
      ]
    },
    {
      name: 'Sala Docente',
      icon: 'fas fa-user-friends',
      roles: ['admin', 'profesor'],
      type: 'group',
      key: 'sala-docente',
      children: [
        { name: 'Sala Docente', href: '/app/sala-docente', icon: 'fas fa-comments', roles: ['admin', 'profesor'] },
        { name: 'Gestión de Reportes', href: '/app/gestion-archivos', icon: 'fas fa-file-alt', roles: ['admin', 'profesor'] }
      ]
    },
    {
      name: 'Comité Evangelístico IBA',
      icon: 'fas fa-hands-praying',
      roles: ['admin', 'profesor', 'estudiante'],
      type: 'group',
      key: 'comite-evangelistico',
      children: [
        { name: 'Comité Evangelístico', href: '/app/comite-evangelistico', icon: 'fas fa-hands-praying', roles: ['admin', 'profesor', 'estudiante'] }
      ]
    },
    {
      name: 'Mi Perfil',
      href: '/app/perfil',
      icon: 'fas fa-user-cog',
      roles: ['admin', 'profesor', 'estudiante'],
      type: 'single'
    },
    {
      name: 'Mis Denarios',
      href: '/app/mis-denarios',
      icon: 'fas fa-coins',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'single'
    },
    {
      name: 'Recursos Bíblicos',
      icon: 'fas fa-bible',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'group',
      key: 'recursos_biblicos',
      children: [
        { name: 'Biblia', href: '/app/biblia', icon: 'fas fa-book-bible', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Biblia en Audio', href: '/app/biblia-audio', icon: 'fas fa-headphones', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Planes de la Biblia', href: '/app/planes-biblia', icon: 'fas fa-book-reader', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Biblia YouVersion', href: '/app/biblia-youversion', icon: 'fas fa-cross', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Pensamientos Bíblicos', href: '/app/pensamientos-biblicos', icon: 'fas fa-book-open', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] }
      ]
    },
    {
      name: 'Genesis AI',
      href: '/app/genesis-ai',
      icon: 'fas fa-robot',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'single',
      requiresGenesisAccess: true  // Solo visible para usuarios con genesis_access o admin
    },
    {
      name: 'Soporte Tecnico IBA',
      href: '/app/soporte-tecnico',
      icon: 'fas fa-headset',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'single'
    },
    {
      name: 'Asociación Graduados IBA',
      icon: 'fas fa-user-graduate',
      roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'],
      type: 'group',
      key: 'graduados',
      requiresGraduado: true, // Solo visible para graduados o admin
      children: [
        { name: 'Directorio de Graduados', href: '/app/graduados/directorio', icon: 'fas fa-address-book', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Grabaciones Exclusivas', href: '/app/graduados/grabaciones', icon: 'fas fa-video', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] },
        { name: 'Devocionales', href: '/app/graduados/devocionales', icon: 'fas fa-pray', roles: ['admin', 'profesor', 'estudiante', 'pastor', 'graduado'] }
      ]
    },
    {
      name: 'A. Administrador',
      icon: 'fas fa-user-shield',
      roles: ['admin', 'profesor', 'estudiante'],
      requiresAdminOrPromotor: true,  // Se mostrará si es admin O promotor
      type: 'group',
      key: 'admin',
      children: [
        // Subgrupo: Usuarios y Accesos
        { name: 'Usuarios y Accesos', type: 'subheader', icon: 'fas fa-users', roles: ['admin', 'profesor', 'estudiante'], requiresAdminOrPromotor: true, submenuKey: 'usuarios' },
        { name: 'Gestión de Usuarios', href: '/app/gestion-usuarios', icon: 'fas fa-users-cog', roles: ['admin'], parentKey: 'usuarios' },
        { name: 'Actividad de Usuarios', href: '/app/actividad-usuarios', icon: 'fas fa-user-clock', roles: ['admin'], parentKey: 'usuarios' },
        { name: 'Gestión de Inactividad', href: '/app/gestion-inactividad', icon: 'fas fa-user-slash', roles: ['admin'], parentKey: 'usuarios' },
        { name: 'Usuarios Pendientes', href: '/app/estudiantes-pendientes', icon: 'fas fa-user-clock', roles: ['admin', 'profesor', 'estudiante'], requiresPromotor: true, parentKey: 'usuarios' },
        { name: 'Área de Consultas', href: '/app/area-consultas', icon: 'fas fa-comments', roles: ['admin', 'profesor', 'estudiante'], requiresPromotor: true, parentKey: 'usuarios' },
        { name: 'Gestión de Admins', href: '/app/gestion-administradores', icon: 'fas fa-crown', roles: ['admin'], parentKey: 'usuarios' },
        { name: 'Gestión Estudiantes Nuevos', href: '/app/gestion-estudiantes-nuevos', icon: 'fas fa-user-graduate', roles: ['admin'], parentKey: 'usuarios' },
        
        // Subgrupo: Gestión Académica
        { name: 'Gestión Académica', type: 'subheader', icon: 'fas fa-graduation-cap', roles: ['admin'], submenuKey: 'academica' },
        { name: 'Panel Control Académico', href: '/app/panel-control-academico', icon: 'fas fa-chart-bar', roles: ['admin'], parentKey: 'academica' },
        { name: 'Planes de Lectura Bíblica', href: '/app/gestion-planes-biblia', icon: 'fas fa-book-bible', roles: ['admin', 'pastor'], parentKey: 'academica' },
        { name: 'Gestión de Canjes', href: '/app/gestion-canjes-denarios', icon: 'fas fa-gift', roles: ['admin', 'profesor'], parentKey: 'academica' },
        { name: 'Gestión de Denarios', href: '/app/gestion-denarios', icon: 'fas fa-coins', roles: ['admin'], parentKey: 'academica' },
        { name: 'Gestión de Referidos', href: '/app/gestion-referidos', icon: 'fas fa-user-friends', roles: ['admin'], parentKey: 'academica' },
        { name: 'Carta de Bienvenida', href: '/app/gestion-carta-bienvenida', icon: 'fas fa-file-pdf', roles: ['admin'], parentKey: 'academica' },
        { name: 'Gestión Horas Profesor', href: '/app/gestion-horas-profesor', icon: 'fas fa-clock', roles: ['admin'], parentKey: 'academica' },
        { name: 'Cumplimiento Plan Estudios', href: '/app/cumplimiento-plan-estudios', icon: 'fas fa-chart-line', roles: ['admin', 'profesor'], parentKey: 'academica' },
        { name: 'Gestionar Foros de Clase', href: '/app/admin/foros-clase', icon: 'fas fa-comment-dots', roles: ['admin'], parentKey: 'academica' },
        { name: 'Libros Genesis AI', href: '/app/admin-genesis-libros', icon: 'fas fa-robot', roles: ['admin'], parentKey: 'academica' },
        
        // Subgrupo: Becas IBA
        { name: 'Becas IBA', type: 'subheader', icon: 'fas fa-hand-holding-usd', roles: ['admin'], submenuKey: 'becas' },
        { name: 'Formulario de Becas', href: '/app/gestion-formulario-becas', icon: 'fas fa-file-alt', roles: ['admin'], parentKey: 'becas' },
        { name: 'Solicitudes de Becas', href: '/app/solicitudes-becas', icon: 'fas fa-clipboard-list', roles: ['admin'], parentKey: 'becas' },
        
        // Subgrupo: Notificaciones
        { name: 'Notificaciones', type: 'subheader', icon: 'fas fa-bell', roles: ['admin'], submenuKey: 'notificaciones' },
        { name: 'Notificaciones Internas', href: '/app/gestion-notificaciones', icon: 'fas fa-bell', roles: ['admin'], parentKey: 'notificaciones' },
        { name: 'Historial de Envíos', href: '/app/historial-notificaciones', icon: 'fas fa-history', roles: ['admin'], parentKey: 'notificaciones' },
        { name: 'Notificaciones Automáticas', href: '/app/notificaciones-automaticas', icon: 'fas fa-robot', roles: ['admin'], parentKey: 'notificaciones' },
        { name: 'Notificaciones Push', href: '/app/notificaciones-push', icon: 'fas fa-bell-on', roles: ['admin'], parentKey: 'notificaciones' },
        { name: 'Anuncio Dashboard', href: '/app/anuncio-dashboard', icon: 'fas fa-bullhorn', roles: ['admin'], parentKey: 'notificaciones' },
        
        // Subgrupo: Landing Page / Sitio Web
        { name: 'Landing Page / Sitio Web', type: 'subheader', icon: 'fas fa-globe', roles: ['admin'], submenuKey: 'landing' },
        { name: 'Gestión Bienvenida Landing', href: '/app/gestion-bienvenida', icon: 'fas fa-home', roles: ['admin'], parentKey: 'landing' },
        { name: 'Secciones Landing Page', href: '/app/gestion-secciones-landing', icon: 'fas fa-layer-group', roles: ['admin'], parentKey: 'landing' },
        { name: 'Banner Flotante', href: '/app/config-banner-flotante', icon: 'fas fa-flag', roles: ['admin'], parentKey: 'landing' },
        { name: 'Banner Clases Semestre', href: '/app/config-banner-semestre', icon: 'fas fa-graduation-cap', roles: ['admin'], parentKey: 'landing' },
        { name: 'Banner Extracurriculares', href: '/app/config-banner-extracurricular', icon: 'fas fa-book-open', roles: ['admin'], parentKey: 'landing' },
        { name: 'Redes Sociales', href: '/app/gestion-redes-sociales', icon: 'fas fa-share-alt', roles: ['admin'], parentKey: 'landing' },
        { name: 'Profesores/Staff', href: '/app/gestion-profesores', icon: 'fas fa-chalkboard-teacher', roles: ['admin'], parentKey: 'landing' },
        { name: 'Gestión Reuniones Profesores', href: '/app/gestion-reuniones-profesores', icon: 'fas fa-video', roles: ['admin'], parentKey: 'landing' },
        
        // Subgrupo: Integraciones
        { name: 'Integraciones', type: 'subheader', icon: 'fas fa-plug', roles: ['admin'], submenuKey: 'integraciones' },
        { name: 'Gestión de Zoom', href: '/app/gestion-zoom', icon: 'fas fa-video', roles: ['admin'], parentKey: 'integraciones' },
        { name: 'Prueba SDK Zoom', href: '/app/zoom-test', icon: 'fas fa-flask', roles: ['admin'], parentKey: 'integraciones' },
        { name: 'Zoom SDK Sandbox (v3.13)', href: '/app/zoom-sdk-sandbox', icon: 'fas fa-vial-circle-check', roles: ['admin'], parentKey: 'integraciones' },
        { name: 'Zoom Embebido (CDN)', href: '/app/clase-zoom-beta?meeting=84749514233&pwd=test&titulo=Prueba%20SDK', icon: 'fas fa-vial', roles: ['admin'], parentKey: 'integraciones' },
        { name: 'Config. Chat Widgets', href: '/app/config-chat-widgets', icon: 'fas fa-comments', roles: ['admin'], parentKey: 'integraciones' },
        { name: 'Webhooks / Zapier', href: '/app/config-webhooks', icon: 'fas fa-bolt', roles: ['admin'], parentKey: 'integraciones' },
        { name: 'Gestión SMS', href: '/app/gestion-sms', icon: 'fas fa-mobile-alt', roles: ['admin'], parentKey: 'integraciones' },
        
        // Subgrupo: Sistema
        { name: 'Sistema', type: 'subheader', icon: 'fas fa-cog', roles: ['admin'], submenuKey: 'sistema' },
        { name: 'Estado del Sistema', href: '/app/estado-sistema', icon: 'fas fa-heartbeat', roles: ['admin'], parentKey: 'sistema' },
        { name: 'Gestión de Backups', href: '/app/gestion-backups', icon: 'fas fa-database', roles: ['admin'], parentKey: 'sistema' },
        { name: 'Scheduler (Tareas Programadas)', href: '/app/gestion-scheduler', icon: 'fas fa-clock', roles: ['admin'], parentKey: 'sistema' },
        { name: 'Historial de Cambios', href: '/app/historial-cambios', icon: 'fas fa-code-branch', roles: ['admin'], parentKey: 'sistema' }
      ]
    }
    ];
  };
  
  const navigationStructure = getNavigationStructure();

  // Debug: mostrar permisos en consola (temporal)
  React.useEffect(() => {
    if (user) {
      console.log('=== DEBUG PERMISOS ===');
      console.log('User:', user.email);
      console.log('Rol:', user.rol);
      console.log('permisos_adicionales:', user.permisos_adicionales);
      console.log('promotor_iba:', user.permisos_adicionales?.promotor_iba);
      console.log('acceso_marketing:', user.permisos_adicionales?.acceso_marketing);
    }
  }, [user]);

  // Función para verificar si el usuario tiene acceso
  const hasAccess = (roles, requiresPromotor = false, requiresAdminOrPromotor = false, requiresGraduado = false, requiresMarketing = false, requiresGenesisAccess = false) => {
    if (!user || !roles) return false;
    
    // Si requiere graduado, verificar el flag es_graduado o ser admin
    if (requiresGraduado) {
      return user.rol === 'admin' || user.es_graduado === true;
    }
    
    // Si requiere admin O promotor (para el grupo principal)
    if (requiresAdminOrPromotor) {
      return user.rol === 'admin' || user.permisos_adicionales?.promotor_iba === true;
    }
    
    // Si requiere promotor, verificar el permiso adicional (para items individuales)
    if (requiresPromotor) {
      return user.rol === 'admin' || user.permisos_adicionales?.promotor_iba === true;
    }

    // Si requiere acceso a marketing
    if (requiresMarketing) {
      return user.rol === 'admin' || user.permisos_adicionales?.acceso_marketing === true;
    }
    
    // Si requiere acceso a Genesis AI
    if (requiresGenesisAccess) {
      return user.rol === 'admin' || user.permisos_adicionales?.genesis_access === true;
    }
    
    return roles.includes(user.rol);
  };

  // Función para renderizar un item individual del menú
  const renderMenuItem = (item, isChild = false, submenuKey = null) => {
    if (!hasAccess(item.roles || ['admin', 'profesor', 'estudiante'], item.requiresPromotor, item.requiresAdminOrPromotor, item.requiresGraduado, item.requiresMarketing, item.requiresGenesisAccess)) {
      return null;
    }

    // Si es un subheader (separador de sección) - estilo diferenciado
    if (item.type === 'subheader') {
      const isExpanded = submenuKey ? expandedSubmenus[submenuKey] : false;
      
      return (
        <div key={item.name} className="mt-2 first:mt-0">
          <button
            onClick={() => submenuKey && toggleSubmenu(submenuKey)}
            className={`w-full group flex items-center px-3 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-200 rounded-lg relative overflow-hidden ${
              isExpanded 
                ? 'bg-blue-200 text-blue-900 shadow-md' 
                : 'bg-blue-100 text-blue-800 hover:bg-blue-150'
            }`}
          >
            <div className={`w-5 h-5 rounded flex items-center justify-center mr-2 ${
              isExpanded ? 'bg-blue-400/40' : 'bg-blue-300/40'
            }`}>
              <i className={`${item.icon} text-[10px] ${isExpanded ? 'text-blue-800' : 'text-blue-600'}`}></i>
            </div>
            <span className="flex-1 text-left">{item.name}</span>
            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-[10px] ml-2 transition-transform duration-200 ${
              isExpanded ? 'text-blue-700' : 'text-blue-500'
            }`}></i>
          </button>
        </div>
      );
    }

    const isSubChild = !!submenuKey;
    const baseClasses = isChild
      ? `group flex items-center py-2 text-sm text-gray-600 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-gray-900 transition-all duration-200 rounded-lg relative overflow-hidden ${isSubChild ? 'px-8 ml-1' : 'px-6 ml-2'}`
      : "group flex items-center px-4 py-3 text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 hover:text-gray-900 transition-all duration-200 rounded-xl relative overflow-hidden";

    const activeClasses = isChild
      ? "bg-gradient-to-r from-blue-100 to-indigo-100 text-gray-900 font-medium shadow-sm border-l-3 border-blue-500"
      : "bg-gradient-to-r from-blue-50 to-purple-50 text-gray-900 font-semibold shadow-md";

    return (
      <NavLink
        key={item.href}
        to={item.href}
        className={({ isActive }) =>
          `${baseClasses} ${isActive ? activeClasses : ''}`
        }
        onClick={() => setMobileMenuOpen(false)}
      >
        <div className={`absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-purple-500 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-200 ${isChild ? '' : 'rounded-r-full'}`}></div>
        {isSubChild ? (
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mr-3 group-hover:bg-blue-500 transition-colors flex-shrink-0"></span>
        ) : (
          <i className={`${item.icon} mr-3 ${isChild ? 'text-sm text-blue-600' : 'text-base text-blue-500'} group-hover:scale-110 transition-transform duration-200`}></i>
        )}
        <span className={`${isChild ? 'text-sm' : 'text-sm font-medium'} flex-1`}>{item.name}</span>
        {/* Badge para Chat IBA (Stream Chat) */}
        {item.href === '/app/chat-iba' && unreadStreamChatCount > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
            {unreadStreamChatCount > 99 ? '99+' : unreadStreamChatCount}
          </span>
        )}
        {/* Badge para Pruebas y Exámenes pendientes */}
        {item.href === '/app/evaluaciones' && pruebasPendientesCount > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-orange-500 text-white rounded-full animate-pulse">
            {pruebasPendientesCount}
          </span>
        )}
        {/* Badge rojo para asignaciones bíblicas pendientes */}
        {item.href === '/app/biblia' && asignacionesBiblicasPendientes > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
            {asignacionesBiblicasPendientes}
          </span>
        )}
        {/* Badge para Pensamientos Bíblicos: admin ve rojo si hay candidato, otros verde si hay nuevo activo sin leer */}
        {item.href === '/app/pensamientos-biblicos' && pensamientoBiblicoTieneNuevo && (
          <span
            className={`ml-2 px-2 py-0.5 text-xs font-bold rounded-full animate-pulse ${user?.rol === 'admin' ? 'bg-amber-500 text-white' : 'bg-green-500 text-white'}`}
            title={user?.rol === 'admin' ? 'Hay un pensamiento pendiente de aprobación' : 'Nuevo pensamiento disponible'}
            data-testid="pensamiento-badge"
          >
            <i className="fas fa-book-open mr-1 text-[10px]"></i>Nuevo
          </span>
        )}
        {/* Badge dorado para Mis Denarios: cantidad de canjes disponibles */}
        {item.href === '/app/mis-denarios' && canjesDisponiblesCount > 0 && (
          <span
            className="ml-2 px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-full shadow-sm"
            title={`Tienes ${canjesDisponiblesCount} premio(s) disponibles para canjear`}
            data-testid="canjes-badge"
          >
            <i className="fas fa-gift mr-1 text-[10px]" />{canjesDisponiblesCount}
          </span>
        )}
      </NavLink>
    );
  };

  // Función para renderizar un grupo de menú expandible
  const renderMenuGroup = (group) => {
    // Verificar acceso incluyendo requiresGraduado y requiresMarketing
    if (!hasAccess(group.roles, false, group.requiresAdminOrPromotor, group.requiresGraduado, group.requiresMarketing)) {
      return null;
    }

    const isExpanded = expandedMenus[group.key];
    const hasVisibleChildren = group.children?.some(child => 
      !child.hidden && hasAccess(child.roles || ['admin', 'profesor', 'estudiante'], child.requiresPromotor, child.requiresAdminOrPromotor || group.requiresAdminOrPromotor, child.requiresGraduado || group.requiresGraduado, child.requiresMarketing || group.requiresMarketing)
    );

    if (!hasVisibleChildren) {
      return null;
    }

    return (
      <div key={group.key} className="mb-2">
        <button
          onClick={() => toggleMenu(group.key)}
          className={`w-full group flex items-center justify-between px-4 py-3 text-gray-800 hover:bg-gradient-to-r hover:from-blue-100 hover:to-purple-100 transition-all duration-200 rounded-xl font-bold relative overflow-hidden shadow-sm ${isExpanded ? 'bg-gradient-to-r from-blue-50 to-purple-50' : ''}`}
        >
          <div className="flex items-center space-x-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${isExpanded ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-gradient-to-br from-blue-400 to-purple-500'} shadow-md group-hover:shadow-lg group-hover:scale-110`}>
              <i className={`${group.icon} text-white text-sm`}></i>
            </div>
            <span className="text-sm font-bold tracking-tight uppercase">{group.name}</span>
          </div>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 ${isExpanded ? 'bg-blue-500 rotate-180' : 'bg-gray-300 group-hover:bg-blue-400'}`}>
            <i className={`fas fa-chevron-down text-white text-xs transition-transform duration-200`}></i>
          </div>
        </button>
        
        {isExpanded && (
          <div className="mt-2 mb-2 space-y-1 bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-xl p-3 border-l-4 border-blue-400 shadow-inner">
            {group.children
              .filter(child => !child.hidden && hasAccess(child.roles || ['admin', 'profesor', 'estudiante'], false, false, group.requiresGraduado))
              .map(child => {
                // Si es un subheader, renderízalo con su submenuKey
                if (child.type === 'subheader') {
                  const submenuKey = child.submenuKey;
                  const isSubmenuExpanded = expandedSubmenus[submenuKey];
                  
                  return (
                    <React.Fragment key={child.name}>
                      {renderMenuItem(child, true, submenuKey)}
                      {/* Renderizar los items hijos de este submenu si está expandido */}
                      {isSubmenuExpanded && group.children
                        .filter(item => item.parentKey === submenuKey && hasAccess(item.roles || ['admin']))
                        .map(subChild => renderMenuItem(subChild, true, submenuKey))
                      }
                    </React.Fragment>
                  );
                }
                
                // Si no tiene parentKey, es un item independiente
                if (!child.parentKey) {
                  return renderMenuItem(child, true);
                }
                
                // Si tiene parentKey pero su parent no está en subheaders, renderízalo
                return null;
              })
            }
          </div>
        )}
      </div>
    );
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification Permission Modal - Shows after login */}
      {showNotificationModal && (
        <NotificationPermissionModal 
          user={user}
          onClose={() => {
            setShowNotificationModal(false);
            setForceNotificationModal(false);
          }}
          forceShow={forceNotificationModal}
        />
      )}
      
      {/* Modal de Renovación - Para estudiantes, graduados y pastores */}
      {user && ['estudiante', 'graduado', 'pastor'].includes(user.rol) && <RenovacionPendiente />}
      
      {/* Desktop Sidebar - Elegant Design */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col h-screen bg-gray-50 shadow-2xl border-r border-gray-200 overflow-y-auto">
          {/* Logo - Header */}
          <div className="flex-shrink-0 px-6 py-6 border-b border-gray-200 bg-white">
            <div className="flex items-center">
              <div className="relative">
                <img
                  src={process.env.REACT_APP_LOGO_URL || "/logo-iba.jpg"}
                  alt="Instituto Logo"
                  className="h-14 w-auto mr-4 drop-shadow-lg"
                />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-300 rounded-full border-2 border-blue-200 shadow-sm animate-pulse"></div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800 leading-tight tracking-wide">
                  Instituto Bíblico Apostólico
                </h1>
                <p className="text-sm text-gray-600 font-medium flex items-center mt-1">
                  <i className="fas fa-graduation-cap mr-1.5 text-blue-400"></i>
                  del Uruguay
                </p>
              </div>
            </div>
          </div>

          {/* User info - Header */}
          <div className="flex-shrink-0 px-6 py-6 border-b border-gray-200 bg-white">
            <div className="flex items-center">
              <div className="relative">
                <div className="relative w-14 h-14 rounded-xl border-2 border-blue-200/50 overflow-hidden bg-blue-50 shadow-lg">
                  {user?.foto_perfil ? (
                    <>
                      {/* Image */}
                      <img
                        src={getImageUrl(user.foto_perfil)}
                        alt="Usuario"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-300 to-sky-300 flex items-center justify-center text-white font-bold text-xl shadow-inner" style={{ display: 'none' }}>
                        {user?.nombre?.charAt(0)?.toUpperCase()}{user?.apellido?.charAt(0)?.toUpperCase()}
                      </div>
                    </>
                  ) : (
                    <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-300 to-sky-300 flex items-center justify-center text-white font-bold text-xl shadow-inner">
                      {user?.nombre?.charAt(0)?.toUpperCase()}{user?.apellido?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-300 rounded-full border-2 border-blue-200 shadow-sm"></div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-base font-bold text-gray-700 leading-tight tracking-wide">
                  {user?.nombre} {user?.apellido}
                </p>
                <p className="text-sm text-gray-600 capitalize font-medium mt-1 flex items-center">
                  <i className="fas fa-crown mr-1.5 text-yellow-300"></i>
                  {user?.rol}
                </p>
              </div>
            </div>
          </div>

          {/* System Info - Compact */}
          <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200">
            <div className="space-y-1.5">
              {/* Fecha y Hora Actual */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5">
                  <i className="fas fa-clock text-emerald-400 text-xs"></i>
                  <span className="text-xs font-semibold text-gray-700">Fecha:</span>
                </div>
                <span className="text-xs font-mono text-gray-600 bg-blue-50 px-1.5 py-0.5 rounded">
                  {currentTime.toLocaleString('es-UY', {
                    timeZone: 'America/Montevideo',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false
                  })}
                </span>
              </div>

              {/* Versión del Sistema */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5">
                  <i className="fas fa-code-branch text-cyan-400 text-xs"></i>
                  <span className="text-xs font-semibold text-gray-700">Versión:</span>
                </div>
                <VersionDisplay />
              </div>

              {/* Estado del Sistema */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5">
                  <i className="fas fa-heartbeat text-pink-400 text-xs"></i>
                  <span className="text-xs font-semibold text-gray-700">Estado:</span>
                </div>
                <div className="flex items-center space-x-1.5 bg-blue-50 px-1.5 py-0.5 rounded">
                  <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse shadow-sm"></div>
                  <span className="text-xs font-mono text-emerald-600">Online</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <div className="flex-1 px-4 py-4">
            <div className="space-y-2">
              {/* Soporte Técnico - Siempre visible arriba */}
              <div className="mb-2">
                <SoporteButton />
              </div>
              {navigationStructure.map((item) => {
                if (item.type === 'single') {
                  return renderMenuItem(item);
                } else if (item.type === 'group') {
                  return renderMenuGroup(item);
                }
                return null;
              })}
            </div>
          </div>

          {/* Logout button */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200 space-y-2">
            <InstallAppButton />
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-300 border border-red-300 rounded-lg"
              data-testid="logout-button"
            >
              <i className="fas fa-sign-out-alt mr-3 text-red-500"></i>
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`lg:hidden fixed left-4 z-50 bg-white shadow-xl hover:shadow-2xl border border-gray-300 transition-all duration-300 ${isSupportSession ? 'top-12' : 'top-4'}`}
            data-testid="mobile-menu-toggle"
          >
            <i className="fas fa-bars text-gray-700"></i>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 p-0 bg-gray-50 border-r border-gray-200">
          <div className="flex flex-col h-full max-h-[100dvh] overflow-hidden">
            {/* Mobile logo */}
            <div className="flex-shrink-0 flex items-center px-6 py-6 border-b border-gray-200 bg-white">
              <div className="relative">
                <img
                  src={process.env.REACT_APP_LOGO_URL || "/logo-iba.jpg"}
                  alt="Instituto Logo"
                  className="h-12 w-auto mr-4 drop-shadow-lg"
                />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-300 rounded-full border-2 border-blue-200 shadow-sm animate-pulse"></div>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-800 leading-tight tracking-wide">
                  Instituto Bíblico Apostólico
                </h1>
                <p className="text-sm text-gray-600 font-medium flex items-center mt-1">
                  <i className="fas fa-graduation-cap mr-1.5 text-blue-400"></i>
                  del Uruguay
                </p>
              </div>
            </div>

            {/* Mobile user info */}
            <div className="flex-shrink-0 px-6 py-6 border-b border-gray-200 bg-white">
              <div className="flex items-center">
                <div className="relative">
                  <div className="relative w-12 h-12 rounded-xl border-2 border-blue-200/50 overflow-hidden bg-blue-50 shadow-lg">
                    {user?.foto_perfil ? (
                      <>
                        <img
                          src={getImageUrl(user.foto_perfil)}
                          alt="Usuario"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-300 to-sky-300 flex items-center justify-center text-white font-bold text-lg shadow-inner" style={{ display: 'none' }}>
                          {user?.nombre?.charAt(0)?.toUpperCase()}{user?.apellido?.charAt(0)?.toUpperCase()}
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-300 to-sky-300 flex items-center justify-center text-white font-bold text-lg shadow-inner">
                        {user?.nombre?.charAt(0)?.toUpperCase()}{user?.apellido?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-300 rounded-full border-2 border-blue-200 shadow-sm"></div>
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-bold text-gray-700 leading-tight tracking-wide">
                    {user?.nombre} {user?.apellido}
                  </p>
                  <p className="text-xs text-gray-600 capitalize font-medium mt-1 flex items-center">
                    <i className="fas fa-crown mr-1.5 text-yellow-300"></i>
                    {user?.rol}
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile System Info - Compact */}
            <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200">
              <div className="space-y-1.5">
                {/* Fecha y Hora Actual - Mobile */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    <i className="fas fa-clock text-emerald-400 text-xs"></i>
                    <span className="text-xs font-semibold text-gray-700">Fecha:</span>
                  </div>
                  <span className="text-xs font-mono text-gray-600 bg-blue-50 px-1.5 py-0.5 rounded">
                    {currentTime.toLocaleString('es-UY', {
                      timeZone: 'America/Montevideo',
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false
                    })}
                  </span>
                </div>

                {/* Versión del Sistema - Mobile */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1.5">
                    <i className="fas fa-code-branch text-cyan-400 text-xs"></i>
                    <span className="text-xs font-semibold text-gray-700">Versión:</span>
                  </div>
                  <VersionDisplay />
                </div>
              </div>
            </div>

            {/* Mobile navigation */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="space-y-2 pb-4">
                {/* Soporte Técnico - Mobile */}
                <div className="mb-2">
                  <SoporteButton />
                </div>
                {navigationStructure.map((item) => {
                  if (item.type === 'single') {
                    return renderMenuItem(item);
                  } else if (item.type === 'group') {
                    return renderMenuGroup(item);
                  }
                  return null;
                })}
              </div>
            </div>

            {/* Mobile logout */}
            <div className="flex-shrink-0 p-4 border-t border-gray-200 space-y-2">
              <InstallAppButton />
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-red-600 hover:bg-red-100 hover:text-red-700 transition-all duration-300 border border-red-300 rounded-lg"
                data-testid="mobile-logout-button"
              >
                <i className="fas fa-sign-out-alt mr-3 text-red-500"></i>
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Support session banner - fixed at top */}
      {isSupportSession && (
        <div className="fixed top-0 left-0 right-0 z-[9998] bg-amber-500 text-white" data-testid="support-session-banner">
          <div className="flex items-center justify-center gap-2 py-1.5 px-4 lg:py-2">
            <i className="fas fa-key text-xs lg:text-sm"></i>
            <span className="text-xs lg:text-sm font-semibold truncate">
              <span className="hidden sm:inline">Sesión de Soporte — Estás viendo como: </span>
              <span className="sm:hidden">Soporte: </span>
              {user?.nombre} {user?.apellido} ({user?.rol})
            </span>
            <button
              onClick={handleLogout}
              className="ml-2 lg:ml-4 px-2 lg:px-3 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs font-bold transition-colors flex-shrink-0"
              data-testid="support-session-exit"
            >
              Salir
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`lg:pl-72 ${isSupportSession ? 'pt-10' : ''}`}>
        {/* Barra global de presencia - usuarios conectados visibles en todas las páginas */}
        {user && (
          <OnlinePresenceBar />
        )}
        <main className="p-4 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Modal de advertencia por inactividad */}
      <InactivityWarningModal 
        isOpen={showWarning}
        onStayConnected={handleStayConnected}
      />

      {/* Receptor global de Pings rápidos (mensajes urgentes admin/profesor) */}
      {user && <QuickPingReceiver />}
      
      {/* Modal de permisos de notificación */}
      {showNotificationModal && (
        <NotificationPermissionModal 
          user={user}
          onClose={() => {
            setShowNotificationModal(false);
            setForceNotificationModal(false);
          }}
          forceShow={forceNotificationModal}
        />
      )}

      {/* Modal de configuración biométrica (solo celulares) */}
      <BiometricPromptModal />

      {/* Modal global de llamada entrante de soporte (solo admins) */}
      {user && user.rol === 'admin' && <SoporteIncomingCall />}

      {/* Modal global de llamada entrante de chat (Stream Chat) — todos los usuarios */}
      {user && <ChatIncomingCall />}
      {/* Listener global de walkie PTT — auto-reproduce si Modo Radio está ON */}
      {user && <PTTWalkieListener />}
      {/* Toast popup de nuevo mensaje de chat — DESHABILITADO (junio 2026)
          Las notificaciones push del navegador/OneSignal siguen funcionando
          igual; solo se quita el popup amarillo dentro de la app que aparecía
          cuando llegaba un mensaje y vos ya estabas usando IBA Connect.
          Para reactivar: descomentar la línea de abajo. */}
      {/* {user && <ChatNewMessageToast />} */}
    </div>
  );
};

export default Layout;
